import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

export const timeEntryZone = "Europe/Oslo";
export interface DriverTimeEntry {
  id: string;
  source: string;
  recordedStart: string | null;
  recordedEnd: string | null;
  reported?: {
    start: string;
    end: string;
    breakMinutes: number;
    comment: string;
  };
  voided?: boolean;
  voidReason?: string;
  periodSeconds: number;
}
export interface DriverTimeEntries {
  entries: DriverTimeEntry[];
  accountLinked: boolean;
  timezone: string;
}

/** Picker dates are calendar labels, not browser-zone instants. */
export function timeEntryWeek(date: Date) {
  const day = dayjs.tz(dayjs(date).format("YYYY-MM-DD"), timeEntryZone);
  const monday = day.subtract((day.day() + 6) % 7, "day").format("YYYY-MM-DD");
  const next = dayjs(monday).add(7, "day").format("YYYY-MM-DD");
  return {
    number: day.isoWeek(),
    from: dayjs.tz(monday, timeEntryZone).toISOString(),
    to: dayjs.tz(next, timeEntryZone).toISOString(),
    label: `${dayjs(monday).format("DD.MM.YYYY")} – ${dayjs(next).subtract(1, "day").format("DD.MM.YYYY")}`,
  };
}
export function todayInOslo() {
  return new Date(dayjs().tz(timeEntryZone).format("YYYY-MM-DD") + "T12:00:00");
}
/** Calendar navigation preserves local dates across DST transitions. */
export function moveTimeEntryWeek(date: Date, direction: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + direction * 7);
  return next;
}

export function timeEntryRange(start?: string | null, end?: string | null) {
  if (!start) return "—";
  const first = dayjs(start).tz(timeEntryZone);
  if (!end) return first.format("HH:mm");
  const last = dayjs(end).tz(timeEntryZone);
  return first.format("YYYY-MM-DD") === last.format("YYYY-MM-DD")
    ? `${first.format("HH:mm")}–${last.format("HH:mm")}`
    : `${first.format("DD.MM.YYYY HH:mm")}–${last.format("DD.MM.YYYY HH:mm")}`;
}

export function timeEntryDay(
  value: string | null | undefined,
  language: string,
) {
  return value
    ? new Intl.DateTimeFormat(language === "no" ? "nb-NO" : language, {
        timeZone: timeEntryZone,
        weekday: "short",
        day: "numeric",
        month: "short",
      }).format(new Date(value))
    : "—";
}

export function hasAdjustedTime(row: DriverTimeEntry) {
  return (
    !!row.reported &&
    !!row.recordedStart &&
    (Date.parse(row.reported.start) !== Date.parse(row.recordedStart) ||
      Date.parse(row.reported.end) !== Date.parse(row.recordedEnd || ""))
  );
}
export function timeEntryStamp(value?: string | null) {
  return value
    ? dayjs(value).tz(timeEntryZone).format("DD.MM.YYYY HH:mm")
    : "—";
}
export function timeEntryDuration(seconds: number) {
  const minutes = Math.round(Math.max(0, seconds) / 60);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
}
export function overlappingTimeEntries(rows: DriverTimeEntry[]) {
  const overlaps = new Set<string>();
  const closed = rows.filter(
    (row) => !row.voided && (row.reported?.end || row.recordedEnd),
  );
  const interval = (row: DriverTimeEntry) => [
    Date.parse(row.reported?.start || row.recordedStart || ""),
    Date.parse(row.reported?.end || row.recordedEnd || ""),
  ];
  closed.forEach((row, i) => {
    const [start, end] = interval(row);
    closed.slice(i + 1).forEach((other) => {
      const [otherStart, otherEnd] = interval(other);
      if (start < otherEnd && otherStart < end) {
        overlaps.add(row.id);
        overlaps.add(other.id);
      }
    });
  });
  return overlaps;
}
